<template>
  <tux-verification
    ref="verificationForm"
    :key="verificationFormKey"
    :auto-submit="!isModal"
    :heading="heading"
    :subheading="subheading"
    :sent-to="sendTo"
    :manual-error-token.sync="otpErrorToken"
    :loading="loading"
    :hide-heading="isModal"
    :show-cancel="!isModal"
    :channel="otpChannel"
    :is-successful="isSuccessful"
    @submit="submit"
    @resend="resend"
    @complete="onVerificationComplete"
    @cancel="cancel" />
</template>

<script>
import nudetect from '@mc-alberta/nudetect'
import { CustomLogger, applyMaskingStyle } from '@mc-alberta/supernova-scripts'
import { TuxVerification } from '@mc-alberta/tuxedo'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { ACTION, LOADERS, NETWORK, ROUTES, VALIDATION_CHANNEL } from '@/constants'
import * as types from '@/store/mutation-types'

export default {
  name: ROUTES.VERIFY_ACCOUNT,
  components: {
    TuxVerification
  },
  props: {
    isModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      otpErrorToken: '',
      is409: this.$route.params.is409,
      isSuccessful: false,
      userFound: this.$route.params.userFound,
      // accept a success callback, defaults to noop function
      onSuccess: this.$route.params.onSuccess || Function.prototype,
      // assign a key to the form component to force re-render on key change
      verificationFormKey: 0
    }
  },
  computed: {
    ...mapState({
      otpChannel: (state) => state.identity.verificationChannelUsed,
      sendTo(state) {
        const contactInfo = state.identity.verificationData.contactInfo

        // if its not a string it is a mobile number object used in SMS OTP
        if (contactInfo && typeof contactInfo !== 'string') {
          return this.formattedUserMobileNumber(this, contactInfo)
        }

        return applyMaskingStyle(contactInfo)
      },
      loading: (state) => state.actionIsPending,
      action: (state) => state.action,
      serviceLayerError: (state) => state.serviceLayerError
    }),
    ...mapGetters(['serviceErrorResponse', 'formattedUserMobileNumber']),
    heading() {
      let heading = null

      if (this.is409) {
        heading = 'verification.headings.existingUser409Heading'
      } else if (this.userFound) {
        heading = 'verification.headings.existingUser'
      } else if (this.action === ACTION.EMAIL_CONFIRMATION) {
        heading = 'verification.headings.emailVerification'
      }

      return heading
    },
    subheading() {
      return this.action === ACTION.EMAIL_CONFIRMATION && !this.userFound
        ? 'verification.subheadings.emailVerification'
        : null
    }
  },
  // when used in modal, sendTo may be initialized as falsy thats why we are skipping this check here. ~ Kerry
  mounted() {
    this.$nextTick(() => nudetect.beginBehaviouralMonitoring())
    this.$root.$on('processingEnd', this.remountForm)

    if (!this.sendTo && !this.isModal) {
      // on refreshing during OTP, redirect the user to entry page as the consumer identity will no longer be present
      this.$router.replace({ name: ROUTES.ENTRY })
    }
  },
  beforeDestroy() {
    this.clearServiceLayerError()
    this.$root.$off('processingEnd', this.remountForm)
  },
  methods: {
    ...mapActions([
      'clearServiceLayerError',
      'startLoader',
      'stopLoader',
      'stopLoaderWithSuccess',
      'validateVerificationCode',
      'fetchVerificationId',
      'logout'
    ]),
    ...mapMutations({
      setInterceptUnauthorized: types.SET_INTERCEPT_UNAUTHORIZED_ERROR,
      setActionIsPending: types.SET_ACTION_IS_PENDING,
      setSessionExpiredModal: types.SET_SESSION_EXPIRED_NOTICE
    }),

    async submit(otpCode) {
      if (this.loading) {
        // submit already inflight, log a 2nd attempt
        CustomLogger.error({
          event: 'MULTI_OTP_SUBMIT_ATTEMPT',
          error: new Error('Attempting to submit OTP multiple times.'),
          details: {
            routeName: this.$route.name,
            routePath: this.$route.path
          }
        })
        return
      }

      // TODO: Migrate to startLoader pattern
      this.setActionIsPending(true)
      this.$analytics.track({
        event: this.$EVENTS.CONTINUE_BUTTON,
        meta: {
          network: NETWORK.MASTERCARD,
          otpChannel: this.otpChannel.toLowerCase()
        }
      })
      this.setInterceptUnauthorized(false)

      this.clearServiceLayerError()

      await this.validateVerificationCode(otpCode)
      nudetect.clearBehaviouralData()

      if (this.serviceLayerError) {
        this.handleError()
        this.stopLoader(LOADERS.RESEND_CODE)
        return
      }

      this.isSuccessful = true
    },

    onVerificationComplete() {
      /**
       * Verifications is being triggered in multiple places. Rather than pollutting
       * it with multiple post verification logic, let's invoke a callback to do
       * what we need to once verification is successful.
       */
      this.$emit('verifySuccess')
      this.setInterceptUnauthorized(true)
      this.onSuccess()
    },

    async resend() {
      if (!this.isModal) {
        this.startLoader(LOADERS.RESEND_CODE)
      }

      // Change this once we add more validation channels and add continue another way text
      this.$analytics.track({
        event:
          this.otpChannel === VALIDATION_CHANNEL.EMAIL
            ? this.$EVENTS.RESEND_OTP_EMAIL
            : this.$EVENTS.RESEND_OTP_SMS,
        meta: {
          network: NETWORK.MASTERCARD,
          otpChannel: this.otpChannel.toLowerCase()
        }
      })
      this.remountForm()
      this.setActionIsPending(true)
      this.clearServiceLayerError()

      await this.fetchVerificationId(this.otpChannel)
      if (this.serviceErrorResponse()) return this.handleError()

      this.stopLoaderWithSuccess(LOADERS.RESEND_CODE)
      this.setActionIsPending(false)
    },

    cancel() {
      this.$router.replace({ name: ROUTES.ENTRY })
    },

    remountForm() {
      this.verificationFormKey++
    },

    handleError() {
      const { status, data } = this.serviceErrorResponse() || {}
      const isInvalidCode = data && data.status === 'INVALID_STATE'

      if (status === 400 && isInvalidCode) {
        nudetect.beginBehaviouralMonitoring()
        this.otpErrorToken = 'verification.error'
      } else {
        this.$emit('verifyError')
        this.$router.replace({ name: ROUTES.ERROR })
      }

      this.setInterceptUnauthorized(true)
      this.setActionIsPending(false)
      this.stopLoader(LOADERS.RESEND_CODE)
    }
  }
}
</script>
